import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useQueryHTTPRequest } from 'api/hooks/useHTTPRequest';
import { getJournalQueryKey } from 'api/utils';
import { useSidebarContext } from 'shared';
import { getQueryString } from 'shared_DEPRECATED/utils';

import { JournalNote, JournalQueryParamsType } from 'features/journal';
import { useIsCoachMode } from 'features/user/hooks/useIsCoachMode';

export const useJournalNotesQuery = (
  filter: Partial<JournalQueryParamsType> = {},
  limit: number = 20
) => {
  const { request } = useQueryHTTPRequest();
  const { participantId } = useSidebarContext();
  const queryParams = useMemo(() => getQueryString(filter), [filter]);

  const isCoachMode = useIsCoachMode();

  const queryKey = getJournalQueryKey({
    queryParams,
    participantId,
    isCoachMode,
  });

  return useInfiniteQuery<JournalNotesResponse>(
    queryKey(),
    async ({ pageParam = 0 }) => {
      const coachPrefix = isCoachMode ? `/coach/${participantId}` : '';

      return await request({
        url: `/api${coachPrefix}/journal/notes?limit=${limit}&offset=${pageParam}&${queryParams}`,
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.flatMap((page) => page.notes).length;

        return lastPage.notes.length === limit ? totalFetched : undefined;
      },
      // @ts-expect-error
      // we are ignoring the type error because there is an issue with select typing for infinite queries
      // More details here: https://github.com/TanStack/query/issues/3065. It will be fixed in the v5
      select: selectNotes,
      staleTime: 1000 * 20,
    }
  );
};

const selectNotes = (data: { pages: JournalNotesResponse[] }) => {
  return data?.pages.flatMap((page) => page.notes);
};

type JournalNotesResponse = {
  notes: JournalNote[];
};
